import { auth } from "../db/firebase";
import { getCifraApiBase } from "./cifraApi";

const MAX_BYTES = 5 * 1024 * 1024;

function signUrl(): string {
  const base = getCifraApiBase().replace(/\/$/, "");
  return base
    ? `${base}/cloudinarySign`
    : `/.netlify/functions/cloudinarySign`;
}

export class CloudinaryUploadError extends Error {
  code?: string;
  constructor(message: string, code?: string) {
    super(message);
    this.name = "CloudinaryUploadError";
    this.code = code;
  }
}

function validateImage(file: File) {
  const looksLikeImage =
    (file.type && file.type.startsWith("image/")) ||
    /\.(jpe?g|png|gif|webp|heic|heif)$/i.test(file.name);

  if (!looksLikeImage) {
    throw new CloudinaryUploadError("Please choose an image file.", "invalid-type");
  }
  if (file.size > MAX_BYTES) {
    throw new CloudinaryUploadError("Image must be under 5MB.", "too-large");
  }
}

/**
 * Upload a profile image to Cloudinary (signed client upload).
 * Returns the secure HTTPS URL.
 */
export async function uploadProfileImage(
  file: File,
  onProgress?: (percent: number) => void,
): Promise<string> {
  validateImage(file);

  const user = auth.currentUser;
  if (!user) {
    throw new CloudinaryUploadError("You must be signed in.", "unauthenticated");
  }

  const signRes = await fetch(signUrl(), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ publicId: user.uid }),
  });

  const signJson = await signRes.json().catch(() => ({}));
  if (!signRes.ok) {
    throw new CloudinaryUploadError(
      signJson.error || "Failed to prepare upload",
      "sign-failed",
    );
  }

  const { cloudName, apiKey, timestamp, signature, folder, publicId } = signJson;
  if (!cloudName || !apiKey || !signature || !timestamp) {
    throw new CloudinaryUploadError("Invalid sign response", "sign-failed");
  }

  const form = new FormData();
  form.append("file", file);
  form.append("api_key", apiKey);
  form.append("timestamp", String(timestamp));
  form.append("signature", signature);
  form.append("folder", folder);
  form.append("public_id", publicId);
  form.append("overwrite", "true");
  form.append("invalidate", "true");

  const uploadEndpoint = `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`;

  const url = await new Promise<string>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", uploadEndpoint);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };
    xhr.onload = () => {
      try {
        const data = JSON.parse(xhr.responseText || "{}");
        if (xhr.status >= 200 && xhr.status < 300 && data.secure_url) {
          resolve(data.secure_url as string);
          return;
        }
        reject(
          new CloudinaryUploadError(
            data.error?.message || "Cloudinary upload failed",
            "upload-failed",
          ),
        );
      } catch {
        reject(new CloudinaryUploadError("Cloudinary upload failed", "upload-failed"));
      }
    };
    xhr.onerror = () =>
      reject(new CloudinaryUploadError("Network error during upload", "network"));
    xhr.send(form);
  });

  return url;
}
